import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, ButtonGroup, Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import BannerCustom from "components/Banners/BannerCustom";
import Modal from "components/Modal/Modal";
import useAppDispatch, { useReduxDispatch } from "hooks/useAppDispatch";
import sandboxIcon from "img/illustrations/general/sandbox.svg";
import CreateSandboxModal from "./components/CreateSandboxModal";
import { switchSandboxSession } from "./model/sandboxAccessRecovery";
import {
  clearSandboxError,
  deleteSandbox,
  fetchSandboxInfo,
} from "./model/sandboxSlice";
import "./Sandbox.sass";

export default function Sandbox() {
  const { t } = useTranslation("productSettings");
  const dispatch = useAppDispatch();
  const reduxDispatch = useReduxDispatch();
  const {
    sandboxes = [],
    isSandboxSession,
    loaded,
    fetching,
    deleting,
    switching,
    error,
  } = useSelector((state) => state.sandbox || {});
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const sandbox = sandboxes[0] || null;
  const busy = deleting || switching;

  useEffect(() => {
    dispatch(fetchSandboxInfo()).catch(() => {});
  }, [dispatch]);

  const switchSession = useCallback(
    async (switchBack = false) => {
      setSuccessMessage("");
      try {
        await switchSandboxSession(dispatch, { switchBack });
      } catch {
        // The Redux error is displayed on the page.
      }
    },
    [dispatch],
  );

  const confirmDelete = useCallback(async () => {
    try {
      await dispatch(deleteSandbox());
      await dispatch(fetchSandboxInfo());
      setDeleteOpen(false);
      setSuccessMessage(
        t("sandbox.manage.deleted", {
          defaultValue: "Sandbox deleted successfully.",
        }),
      );
    } catch {
      // Keep confirmation open and show the server error.
    }
  }, [dispatch, t]);

  const clearError = () => reduxDispatch(clearSandboxError());

  if (!loaded && fetching) {
    return (
      <div className="Sandbox Sandbox--loading">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="Sandbox">
      {error && !deleteOpen ? (
        <BannerCustom status="critical" message={error} onDismiss={clearError} />
      ) : null}
      {successMessage ? (
        <BannerCustom
          status="success"
          message={successMessage}
          onDismiss={() => setSuccessMessage("")}
        />
      ) : null}

      <div className="Sandbox__intro">
        <div>
          <h2>
            {t("sandbox.manage.title", { defaultValue: "Manage sandboxes" })}
          </h2>
          <p>
            {t("sandbox.manage.description", {
              defaultValue:
                "Create an isolated environment for testing without changing your live company data.",
            })}
          </p>
        </div>
        {!sandbox && loaded && !fetching && !error ? (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t("sandbox.navigation.create", {
              defaultValue: "Create sandbox",
            })}
          </Button>
        ) : null}
      </div>

      {fetching && loaded ? (
        <div className="Sandbox__refreshing">
          <Spinner size="small" />
        </div>
      ) : null}

      {!sandbox && !fetching && !error ? (
        <div className="Sandbox__emptyCard">
          <div className="Sandbox__emptyIcon">
            <img src={sandboxIcon} alt="" />
          </div>
          <h3>
            {t("sandbox.manage.emptyTitle", {
              defaultValue: "No sandbox yet",
            })}
          </h3>
          <p>
            {t("sandbox.manage.emptyDescription", {
              defaultValue:
                "Create a sandbox to safely test settings and workflows.",
            })}
          </p>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t("sandbox.navigation.create", {
              defaultValue: "Create sandbox",
            })}
          </Button>
        </div>
      ) : null}

      {sandbox ? (
        <div className="Sandbox__managementCard">
          <div className="Sandbox__identity">
            <div className="Sandbox__identityIcon">
              <img src={sandboxIcon} alt="" />
            </div>
            <div>
              <div className="Sandbox__nameRow">
                <h3>{sandbox.name}</h3>
                {isSandboxSession ? (
                  <Badge tone="success">
                    {t("sandbox.navigation.active", {
                      defaultValue: "Active sandbox",
                    })}
                  </Badge>
                ) : null}
              </div>
              <p>
                {t("sandbox.manage.sandboxDescription", {
                  defaultValue: "Isolated from your live company data",
                })}
              </p>
            </div>
          </div>

          <div className="Sandbox__actions">
            <Button
              variant="primary"
              loading={switching}
              disabled={deleting}
              onClick={() => switchSession(isSandboxSession)}
            >
              {isSandboxSession
                ? t("sandbox.navigation.switchBack", {
                    defaultValue: "Switch back to live company",
                  })
                : t("sandbox.navigation.open", {
                    defaultValue: "Switch to this sandbox",
                  })}
            </Button>
            <Button
              tone="critical"
              disabled={busy || isSandboxSession}
              onClick={() => {
                clearError();
                setDeleteOpen(true);
              }}
            >
              {t("sandbox.manage.delete", { defaultValue: "Delete sandbox" })}
            </Button>
          </div>

          {isSandboxSession ? (
            <div className="Sandbox__activeHint">
              {t("sandbox.manage.deleteActiveHint", {
                defaultValue:
                  "Switch back to your live company before deleting this sandbox.",
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {error && !fetching && !sandbox ? (
        <div className="Sandbox__recovery">
          <Button onClick={() => dispatch(fetchSandboxInfo()).catch(() => {})}>
            {t("sandbox.manage.retry", { defaultValue: "Try again" })}
          </Button>
          {isSandboxSession ? (
            <Button
              variant="primary"
              loading={switching}
              onClick={() => switchSession(true)}
            >
              {t("sandbox.navigation.switchBack", {
                defaultValue: "Switch back to live company",
              })}
            </Button>
          ) : null}
        </div>
      ) : null}

      <CreateSandboxModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() =>
          setSuccessMessage(
            t("sandbox.manage.created", {
              defaultValue: "Sandbox created successfully.",
            }),
          )
        }
      />

      <Modal
        title={`${t("sandbox.manage.deleteTitle", {
          defaultValue: "Delete sandbox",
        })}?`}
        visible={deleteOpen}
        onClose={() => {
          if (deleting) return;
          setDeleteOpen(false);
          clearError();
        }}
        iconType="danger"
        description={t("sandbox.manage.deleteDescription", {
          defaultValue:
            "This sandbox and all of its data will be permanently deleted. This action cannot be undone.",
        })}
        content={
          error ? <BannerCustom status="critical" message={error} /> : null
        }
        footer={
          <ButtonGroup fullWidth>
            <Button
              size="large"
              disabled={deleting}
              onClick={() => {
                setDeleteOpen(false);
                clearError();
              }}
            >
              {t("sandbox.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              variant="primary"
              tone="critical"
              size="large"
              loading={deleting}
              onClick={confirmDelete}
            >
              {t("sandbox.manage.delete", {
                defaultValue: "Delete sandbox",
              })}
            </Button>
          </ButtonGroup>
        }
      />
    </div>
  );
}
